import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { format } from "date-fns";
import { Search, Download, Calendar, CreditCard, Users, Activity } from "lucide-react";

interface UserHistoryData {
  id: string;
  username: string;
  full_name: string;
  email: string;
  signup_date: string;
  account_age_days: number;
  subscription_type: string;
  subscription_status: string;
  next_payment_date: string | null;
  days_until_payment: number | null;
  current_period_start: string | null;
  credits_balance: number;
  credits_used: number;
  total_credits_received: number;
  next_reset_date: string | null;
  days_until_reset: number | null;
  last_reset_date: string | null;
  all_time_token_usage: number;
  current_month_token_usage: number;
  total_operations: number;
}

interface UserHistoryPanelProps {
  refreshTrigger?: number;
}

export default function UserHistoryPanel({ refreshTrigger }: UserHistoryPanelProps) {
  const [users, setUsers] = useState<UserHistoryData[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<UserHistoryData[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    fetchUserHistory();
  }, [refreshTrigger]);

  useEffect(() => {
    filterUsers();
  }, [searchTerm, users]);

  const fetchUserHistory = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase.rpc('get_user_history_data');
      
      if (error) {
        console.error("Error fetching user history:", error);
        toast.error("Failed to load user history data");
        return;
      }

      setUsers(data || []);
    } catch (err) {
      console.error("Error in fetchUserHistory:", err);
      toast.error("An unexpected error occurred");
    } finally {
      setLoading(false);
    }
  };

  const filterUsers = () => {
    if (!searchTerm) {
      setFilteredUsers(users);
      return;
    }

    const filtered = users.filter(user =>
      user.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.subscription_type?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredUsers(filtered);
  };

  const exportToCSV = () => {
    const headers = [
      "Username", "Full Name", "Email", "Signup Date", "Account Age (Days)",
      "Subscription Type", "Subscription Status", "Next Payment", "Days Until Payment",
      "Credits Balance", "Credits Used", "Total Credits Received",
      "Next Reset", "Days Until Reset", "All-Time Tokens", "Current Month Tokens", "Total Operations"
    ];

    const csvData = filteredUsers.map(user => [
      user.username || '',
      user.full_name || '',
      user.email || '',
      user.signup_date ? format(new Date(user.signup_date), 'yyyy-MM-dd') : '',
      user.account_age_days || 0,
      user.subscription_type || '',
      user.subscription_status || '',
      user.next_payment_date ? format(new Date(user.next_payment_date), 'yyyy-MM-dd') : '',
      user.days_until_payment || '',
      user.credits_balance || 0,
      user.credits_used || 0,
      user.total_credits_received || 0,
      user.next_reset_date ? format(new Date(user.next_reset_date), 'yyyy-MM-dd') : '',
      user.days_until_reset || '',
      user.all_time_token_usage || 0,
      user.current_month_token_usage || 0,
      user.total_operations || 0
    ]);

    const csvContent = [headers, ...csvData]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `user-history-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const getSubscriptionBadge = (type: string, status: string) => {
    if (status === 'inactive') {
      return <Badge variant="outline">Inactive</Badge>;
    }
    
    switch (type) {
      case 'yearly':
        return <Badge variant="default">Yearly</Badge>;
      case 'monthly':
        return <Badge variant="secondary">Monthly</Badge>;
      case 'free':
        return <Badge variant="outline">Free</Badge>;
      default:
        return <Badge variant="outline">{type}</Badge>;
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return format(new Date(dateString), 'MMM dd, yyyy');
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Users</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{users.length}</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Subscriptions</CardTitle>
            <CreditCard className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {users.filter(u => u.subscription_status === 'active' && u.subscription_type !== 'free').length}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Token Usage</CardTitle>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {users.reduce((sum, u) => sum + (u.all_time_token_usage || 0), 0).toLocaleString()}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">This Month Tokens</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {users.reduce((sum, u) => sum + (u.current_month_token_usage || 0), 0).toLocaleString()}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Controls */}
      <Card>
        <CardHeader>
          <CardTitle>User History</CardTitle>
          <CardDescription>
            Comprehensive view of user registration, subscription, and usage data
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
              <Input
                placeholder="Search by username, email, or subscription type..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Button onClick={exportToCSV} variant="outline">
              <Download className="h-4 w-4 mr-2" />
              Export CSV
            </Button>
          </div>

          {/* Table */}
          <div className="rounded-md border overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead>Account Info</TableHead>
                  <TableHead>Subscription</TableHead>
                  <TableHead>Credits</TableHead>
                  <TableHead>Token Usage</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredUsers.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>
                      <div>
                        <div className="font-medium">{user.username || 'N/A'}</div>
                        <div className="text-sm text-muted-foreground">{user.full_name || 'N/A'}</div>
                        <div className="text-xs text-muted-foreground">{user.email}</div>
                      </div>
                    </TableCell>
                    
                    <TableCell>
                      <div className="space-y-1">
                        <div className="text-sm">
                          <span className="font-medium">Signed up:</span> {formatDate(user.signup_date)}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {user.account_age_days} days ago
                        </div>
                      </div>
                    </TableCell>
                    
                    <TableCell>
                      <div className="space-y-2">
                        {getSubscriptionBadge(user.subscription_type, user.subscription_status)}
                        {user.next_payment_date && (
                          <div className="text-xs text-muted-foreground">
                            Next payment: {formatDate(user.next_payment_date)}
                            {user.days_until_payment !== null && (
                              <span className="block">({user.days_until_payment} days)</span>
                            )}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    
                    <TableCell>
                      <div className="space-y-1">
                        <div className="text-sm">
                          <span className="font-medium">Balance:</span> {user.credits_balance}
                        </div>
                        <div className="text-sm">
                          <span className="font-medium">Used:</span> {user.credits_used}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          Total received: {user.total_credits_received}
                        </div>
                        {user.next_reset_date && (
                          <div className="text-xs text-muted-foreground">
                            Reset: {formatDate(user.next_reset_date)}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    
                    <TableCell>
                      <div className="space-y-1">
                        <div className="text-sm">
                          <span className="font-medium">All-time:</span> {user.all_time_token_usage?.toLocaleString() || 0}
                        </div>
                        <div className="text-sm">
                          <span className="font-medium">This month:</span> {user.current_month_token_usage?.toLocaleString() || 0}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {user.total_operations} operations
                        </div>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          
          {filteredUsers.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              {searchTerm ? "No users found matching your search." : "No user data available."}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}